import React, { useMemo, useState } from 'react';
import {
  MuiBox,
  Button,
  MuiTable,
  Typography,
  SvgIcon,
  SvgIconButton,
  Card,
  Box,
  Tooltip
} from '@kis-technologies/kis-ui';
import { VenueTableProps } from '../types';
import { useStyles } from '../styled';

const VenueTaxesTable = ({ taxes, onCreate, onEdit, onDelete, disabled, venues }: VenueTableProps) => {
  const classes = useStyles();
  const [sortParams, setSortParams] = useState<{ column: string; order: 'asc' | 'desc' }>({
    column: 'name',
    order: 'asc'
  });

  const mappedTaxes = taxes.map((tax) => {
    const venue = venues?.find((v: { id: string | null }) => v.id === tax.venueId);

    return {
      ...tax,
      name: venue?.name || tax.venueId
    };
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sort = (data: any[], column: string, order: string) => {
    return [...data].sort((a, b) => {
      let valA = a[column] ?? '';
      let valB = b[column] ?? '';

      if (typeof valA === 'string' && typeof valB === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedTaxes = useMemo(() => {
    return sort(mappedTaxes, sortParams.column, sortParams.order);
  }, [mappedTaxes, sortParams]);

  const SortArrow = ({ column }: { column: string }) => (
    <Box display="inline-block" onClick={() => handleHeaderClick(column)} style={{ cursor: 'pointer' }}>
      <SvgIcon name={sortParams.order === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: 5 }} />
    </Box>
  );

  const handleHeaderClick = (column: string) => {
    setSortParams((prev) => ({
      column,
      order: prev.column === column && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <MuiBox>
      <MuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight="bold">Venue Taxes</Typography>
        {!disabled && (
          <Button onClick={onCreate} disabled={disabled}>
            Create Venue Tax
          </Button>
        )}
      </MuiBox>
      <MuiBox className={classes.venueTaxDetailsOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer>
              <MuiTable.Table stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      className={classes.venueTaxDetailsHeaders}
                      onClick={() => handleHeaderClick('name')}
                      width="30%"
                    >
                      Venue
                      {sortParams.column === 'name' && <SortArrow column="name" />}
                    </MuiTable.TableCell>{' '}
                    <MuiTable.TableCell
                      className={classes.venueTaxDetailsHeaders}
                      onClick={() => handleHeaderClick('label')}
                      width="35%"
                    >
                      Label
                      {sortParams.column === 'label' && <SortArrow column="label" />}
                    </MuiTable.TableCell>{' '}
                    <MuiTable.TableCell
                      align="center"
                      className={classes.venueTaxDetailsHeaders}
                      onClick={() => handleHeaderClick('percentage')}
                      width="20%"
                    >
                      Percentage
                      {sortParams.column === 'percentage' && <SortArrow column="percentage" />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell align="center" width="10%" className={classes.venueTaxDetailsHeaders}>
                      Actions
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>

                <MuiTable.TableBody>
                  {sortedTaxes.length === 0 && (
                    <MuiTable.TableRow>
                      <MuiTable.TableCell colSpan={4} align="center">
                        No venue taxes found
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  )}

                  {sortedTaxes.map((tax) => (
                    <MuiTable.TableRow key={tax.venueId}>
                      <MuiTable.TableCell>{tax.name}</MuiTable.TableCell>
                      <MuiTable.TableCell>{tax.label}</MuiTable.TableCell>
                      <MuiTable.TableCell align="center">{tax.percentage}%</MuiTable.TableCell>
                      <MuiTable.TableCell align="center">
                        <MuiBox display="flex" gap={1} justifyContent="center">
                          <Tooltip title="Edit VenueTax" placement="top">
                            <SvgIconButton onClick={() => onEdit(tax)}>
                              <SvgIcon name="edit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          {!disabled && (
                            <Tooltip title="Delete VenueTax" placement="top">
                              <SvgIconButton onClick={() => onDelete(tax)} disabled={disabled}>
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          )}
                        </MuiBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>
    </MuiBox>
  );
};

export default VenueTaxesTable;
